import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// In-memory token store (not localStorage — cleared on tab close)
let _accessToken = null;
let _refreshToken = null;

export function setTokens(access, refresh) {
  _accessToken = access;
  _refreshToken = refresh;
}

export function clearTokens() {
  _accessToken = null;
  _refreshToken = null;
}

export function getAccessToken() {
  return _accessToken;
}

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  timeout: 15000, // 15s — surfaces hung backend / ingress instead of freezing the tab
});

// ─── Request interceptor: attach Bearer token from memory ───
api.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

// ─── Response interceptor: handle 401 with token refresh + retry-once on 502/503/504 ───
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// Retry transient ingress hiccups exactly once with a small backoff.
const TRANSIENT_STATUSES = new Set([502, 503, 504]);
const TRANSIENT_RETRY_METHODS = new Set(['get', 'head', 'options']);

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    // ── Retry-once for transient 5xx on idempotent verbs ──
    if (
      originalRequest &&
      !originalRequest._transientRetried &&
      TRANSIENT_STATUSES.has(error.response?.status) &&
      TRANSIENT_RETRY_METHODS.has((originalRequest.method || 'get').toLowerCase())
    ) {
      originalRequest._transientRetried = true;
      await new Promise((r) => setTimeout(r, 600));
      return api(originalRequest);
    }

    if (originalRequest?.url?.includes('/auth/')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (!_refreshToken) {
        clearTokens();
        if (typeof window !== 'undefined') window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(
          `${API_URL}/api/auth/refresh`,
          { refresh_token: _refreshToken },
          { withCredentials: true, timeout: 15000 }
        );
        setTokens(data.token, data.refresh_token);
        processQueue(null, data.token);
        originalRequest.headers.Authorization = `Bearer ${data.token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        clearTokens();
        if (typeof window !== 'undefined') window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
